import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { useParams, useNavigate } from 'react-router';
import useResponsive from '../../../utils/useResponsive';
import { brandDnaActions } from '../../../actions/brandDna';
import ThreadSidebar from './components/ThreadSidebar';
import ConversationChat from './components/ConversationChat';
import AssetPanel from './components/AssetPanel';
import MobileHeader from './components/MobileHeader';
import './agent.css';

class AgentErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[AgentScreen] Render error:', error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#050505', fontFamily: "'Inter', sans-serif", gap: 16 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16,
          background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF' }}>Something went wrong</div>
        <div style={{ fontSize: 13, color: '#94A3B8', maxWidth: 340, textAlign: 'center' }}>
          {this.state.error?.message || 'An unexpected error occurred in the AI Agent.'}
        </div>
        <button
          onClick={() => this.setState({ hasError: false, error: null })}
          style={{ marginTop: 8, padding: '8px 20px', borderRadius: 8, cursor: 'pointer', background: 'linear-gradient(to right, #FF7E67, #FF4D8D)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600 }}
        >Try again</button>
      </div>
    );
  }
}

export default function AgentScreen() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null); // sidebar thread-list filter only
  const [activeThread, setActiveThread] = useState(null);       // source of truth for current thread
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [generationsOpen, setGenerationsOpen] = useState(false);
  const [completedImages, setCompletedImages] = useState({});
  const [pendingJobIds, setPendingJobIds] = useState([]);
  const useAsInputRef = useRef(null); // callback set by ConversationChat for cross-panel "use as input"

  const fetchBrands = useCallback(() => {
    setLoadError(null);
    setLoading(true);
    brandDnaActions.listBrands()
      .then(res => {
        if (res?.success) {
          const list = res.data || [];
          setBrands(list);
          if (list.length > 0) {
            const def = list.find(b => b.is_default) || list[0];
            setSelectedBrandId(def.brand_id);
          }
        }
      })
      .catch(() => setLoadError('Unable to connect to the server. Check your connection and try again.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchBrands(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset active thread + pending jobs when navigating to a different thread
  useEffect(() => {
    setActiveThread(null);
    setPendingJobIds([]);
  }, [threadId]);

  const handleNewJobIds = useCallback((ids) => {
    setPendingJobIds(prev => [...prev, ...ids]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleThreadLoaded = useCallback((thread) => setActiveThread(thread), []);

  // activeBrand is derived from the loaded thread, not the sidebar selector
  const activeBrandId = activeThread?.metadata?.brand_id || activeThread?.brand_id || null;
  const activeBrand   = brands.find(b => b.brand_id === activeBrandId) || null;

  // selectedBrand is only used when creating a new thread (no activeThread yet)
  const selectedBrand = brands.find(b => b.brand_id === selectedBrandId);
  const hasNoBrands = !loading && !loadError && brands.length === 0;

  if (loadError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#050505', fontFamily: "'Inter', sans-serif", gap: 16 }}>
        <div style={{ fontSize: 32 }}>📡</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF' }}>Can't reach the server</div>
        <div style={{ fontSize: 13, color: '#94A3B8', maxWidth: 340, textAlign: 'center' }}>{loadError}</div>
        <button
          onClick={fetchBrands}
          style={{ marginTop: 8, padding: '8px 20px', borderRadius: 8, cursor: 'pointer', background: 'linear-gradient(to right, #FF7E67, #FF4D8D)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600 }}
        >Retry</button>
      </div>
    );
  }

  return (
    <AgentErrorBoundary>
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: '100%', overflow: 'hidden', position: 'relative', background: '#050505', fontFamily: "'Inter', sans-serif", zIndex: 0 }}>
      <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(ellipse at 0% 0%, rgba(26,11,46,0.7) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 }} />

      {/* Desktop sidebar (always visible) */}
      {!isMobile && (
        <ThreadSidebar
          brands={brands}
          selectedBrandId={selectedBrandId}
          onSelectBrand={setSelectedBrandId}
          selectedThreadId={threadId}
          navigate={navigate}
          onClose={() => setSidebarOpen(false)}
          isMobile={false}
        />
      )}

      {/* Mobile sidebar drawer */}
      {isMobile && (
        <>
          <div className={`mobile-drawer-backdrop ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
          <div className={`mobile-drawer ${sidebarOpen ? 'open' : ''}`}>
            <ThreadSidebar
              brands={brands}
              selectedBrandId={selectedBrandId}
              onSelectBrand={setSelectedBrandId}
              selectedThreadId={threadId}
              navigate={navigate}
              onClose={() => setSidebarOpen(false)}
              isMobile={true}
            />
          </div>
        </>
      )}

      {/* Main chat area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative', zIndex: 1 }}>
        {/* Mobile header */}
        {isMobile && (
          <MobileHeader
            brandName={(activeBrand || selectedBrand)?.brand_name}
            threadTitle={activeThread?.title}
            onOpenSidebar={() => setSidebarOpen(true)}
            onOpenGenerations={() => setGenerationsOpen(true)}
            completedCount={Object.keys(completedImages).length}
          />
        )}

        <ConversationChat
          threadId={threadId}
          selectedBrandId={selectedBrandId}
          selectedBrand={activeBrand || selectedBrand}
          navigate={navigate}
          hasNoBrands={hasNoBrands}
          isMobile={isMobile}
          onOpenSidebar={() => setSidebarOpen(true)}
          completedImages={completedImages}
          onNewJobIds={handleNewJobIds}
          onThreadLoaded={handleThreadLoaded}
          useAsInputRef={useAsInputRef}
        />
      </div>

      {/* Desktop generation panel */}
      {!isMobile && (
        <div style={{ width: 300, flexShrink: 0, position: 'relative', zIndex: 1 }}>
          <AssetPanel
            setCompletedImages={setCompletedImages}
            pendingJobIds={pendingJobIds}
            threadId={threadId}
            threadBrandId={activeBrandId || selectedBrandId}
            onUseAsInput={(url) => useAsInputRef.current?.(url)}
          />
        </div>
      )}

      {/* Mobile generation bottom sheet */}
      {isMobile && (
        <>
          <div
            className={`mobile-drawer-backdrop ${generationsOpen ? 'open' : ''}`}
            onClick={() => setGenerationsOpen(false)}
          />
          <div className={`mobile-bottom-sheet ${generationsOpen ? 'open' : ''}`}>
            <div className="sheet-handle" onClick={() => setGenerationsOpen(false)} />
            <AssetPanel
              setCompletedImages={setCompletedImages}
              pendingJobIds={pendingJobIds}
              threadId={threadId}
              threadBrandId={activeBrandId || selectedBrandId}
              onUseAsInput={(url) => { useAsInputRef.current?.(url); setGenerationsOpen(false); }}
            />
          </div>
        </>
      )}
    </div>
    </AgentErrorBoundary>
  );
}

